<template>
  <div>
    <div class="container">
      <div class="row">
        <div class="col-lg-12 px-0" :class="`col-xl-${tocHtml.length > 0 ? '9' : '12'}`">
          <div class="bd-content">
            <div id="nav-scroller" ref="content" class="content-container">
              <template v-if="guides && from === 'provider'">
                <h2>{{ $t('M05D-02-01b') }}</h2>
              </template>
              <template v-if="from === 'api'">
                <h2 v-if="apiName" class="mb-3">{{ apiName }}</h2>
                <router-link
                  v-if="apiInfo[0] && apiInfo[0].swagger_file"
                  class="d-flex align-items-center cursor-pointer theme-link"
                  :to="{
                    name: $route.name,
                    params: {
                      provider_name: $route.params.provider_name,
                      api_name: $route.params.api_name,
                      tab_name: 'endpoint'
                    }
                  }"
                >
                  <b>{{ $t('M08d-07') }}</b>
                  <i class="fas fa-external-link-alt text-right sidebar-icons ml-2" />
                </router-link>
              </template>
              <hr v-if="guides || from === 'api'" class="my-5">
              <Viewer
                v-if="guides"
                id="rendered-html"
                ref="toastuiViewer"
                api-key="no-api-key"
                :initialValue="guides"
                :options="viewerOptions"
                height="100%"
                previewStyle="vertical"
                class="readme-html"
                @load="renderReadme"
                @change="renderReadme"
              />
              <!-- <div v-show="guides" id="rendered-html" :key="`guides-${Date.now()}`" class="readme-html" /> -->
              <div v-if="!guides">
                <p>{{ noInfoMessage }}</p>
              </div>
              <section v-if="from === 'api'" ref="section" class="more-api-section mt-7 theme-readme-link-to-provider-section">
                <h5>{{ $t('M08d-08-01') }}</h5>
                <p class="mb-0">{{ $t('M08d-08-02') }}
                  <a class="cursor-pointer theme-link" @click="goToProviderGuides"><b>{{ $t('M08d-08-03') }}</b></a>{{ $t('M08d-08-04') }}
                </p>
              </section>
              <section v-if="last_update_date && guides" ref="section" class="last-updated-container text-muted">
                <i class="fas fa-clipboard-list icons-right" />{{ $t('M08d-09', { date: last_update_date }) }}
              </section>
            </div>
          </div>
        </div>
        <nav v-if="guides" aria-label="Secondary navigation" class="col-xl-3 d-none pl-xl-5" :class="`d-xl-${tocHtml.length > 0 ? 'block' : 'none'}`">
          <div class="sticky font-size-1">
            <span class="readme-menu-text">
              <i class="fas fa-align-left icons-right" />CONTENTS
            </span>
            <div id="toc" class="readme-menu-items" v-html="tocHtml" />
          </div>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import Clipboard from 'clipboard'
import 'codemirror/lib/codemirror.css'
import '@toast-ui/editor/dist/toastui-editor.css'
import 'highlight.js/styles/stackoverflow-light.css'
import hljs from 'highlight.js'
import { Viewer } from '@toast-ui/vue-editor'
import codeSyntaxHighlight from '@toast-ui/editor-plugin-code-syntax-highlight'
import { handleFailImg } from '@/directive/img-handler/img-handler'
import { getDisplayDate } from '@/utils/date-time-util'

export default {
  name: 'Readme',
  components: {
    Viewer
  },
  props: {
    guides: {
      type: String,
      default: ''
    },
    lastUpdateDate: {
      type: String,
      default: ''
    },
    from: {
      type: String,
      default: ''
    },
    apiName: {
      type: String,
      default: ''
    },
    apiInfo: {
      type: Array,
      default: () => []
    },
    themeColor: {
      type: String,
      default: '#57ABF0'
    },
    noInfoMessage: {
      type: String,
      default: 'There is no further information on this page.'
    }
  },
  data() {
    return {
      html: '',
      tocHtml: '',
      last_update_date: getDisplayDate(this.lastUpdateDate),
      headings: [],
      viewerOptions: {
        plugins: [
          [codeSyntaxHighlight, { hljs }]
        ]
      }
    }
  },
  watch: {
    themeColor() {
      if (this.headings && this.headings.length > 0) {
        document.querySelector(`.readme-menu-item#${this.headings[0].getAttribute('id')}`).style.color = this.themeColor
      }
    }
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    renderReadme() {
      document.querySelectorAll('.tui-editor-contents a') !== null ? document.querySelectorAll('.tui-editor-contents a').forEach(link => {
        var hyperlink = link.getAttribute('href')
        !hyperlink.startsWith('http') ? link.setAttribute('href', `https://${hyperlink}`) : null
      }) : null
      this.handleReadmeFailImg()
      this.addCopyToCodeBlock()
      this.tocHtml = this.generateLinkMarkup(document.querySelector('.tui-editor-contents'))
      this.$nextTick(() => {
        this.addClickEvent()
        if (this.headings && this.headings.length > 0) {
          document.querySelector(`.readme-menu-item#${this.headings[0].getAttribute('id')}`).style.color = this.themeColor
          window.addEventListener('scroll', this.handleScroll)
        }
      })
    },
    generateLinkMarkup($contentElement) {
      const headings = [...$contentElement.querySelectorAll('h1, h2')]
      headings.forEach((heading, index) => {
        heading.setAttribute('id', document.getElementById(`${this.formatString(heading.innerText)}`) !== null ? `id-${this.formatString(heading.innerText)}-${Date.now()}-${index}` : `id-${this.formatString(heading.innerText)}`)
      })
      this.headings = headings
      const parsedHeadings = headings.map(heading => {
        return {
          title: heading.innerText,
          depth: heading.nodeName.replace(/\D/g, ''),
          id: heading.getAttribute('id')
        }
      })
      const htmlMarkup = parsedHeadings.map(h => `
        <li class="nav-item toc-entry cursor-pointer readme-menu-text readme-menu-text-link">
          <a id="${this.formatString(h.id)}" class="readme-menu-item nav-link ${h.depth > 1 ? 'py-0 pr-0 pl-2' : 'p-0'}">${h.title}</a>
        </li>
      `)
      const finalMarkup = htmlMarkup.length > 0 ? `<ul class="nav section-nav flex-column">${htmlMarkup.join('')}</ul>` : ''
      return finalMarkup
    },
    addClickEvent() {
      var elements = document.getElementsByClassName('readme-menu-item')
      elements.length > 0 ? Array.prototype.forEach.call(elements, e => {
        e.addEventListener('click', () => {
          this.scrollToSection(this.formatString(e.getAttribute('id')))
        })
      }) : null
    },
    handleScroll() {
      var tempHeadings = this.headings
      var currentHeading = tempHeadings.sort(function(a, b) { return Math.abs(a.getBoundingClientRect().top) - Math.abs(b.getBoundingClientRect().top) })[0]
      tempHeadings.forEach((heading) => {
        var contentHeadingNav = document.querySelector(`.readme-menu-item#${heading.getAttribute('id')}`)
        if (contentHeadingNav) {
          contentHeadingNav.style.color = heading === currentHeading ? this.themeColor : '#505050'
        }
      })
    },
    handleReadmeFailImg() {
      document.querySelectorAll('.tui-editor-contents img') !== null ? document.querySelectorAll('.tui-editor-contents img').forEach(img => {
        img.onerror = () => {
          console.log('readme img error')
          handleFailImg(img, 'large', '', 'default-ratio')
        }
      }) : null
    },
    addCopyToCodeBlock() {
      document.querySelectorAll('pre > code').forEach(function(codeBlock, index) {
        var codeBlockId = `code-block-${index}`
        codeBlock.setAttribute('id', codeBlockId)
        var button = document.createElement('button')
        button.className = 'copy-code-button btn btn-xs btn-soft-secondary'
        button.type = 'button'
        button.innerText = 'Copy'
        button.setAttribute('data-clipboard-target', `#${codeBlockId}`)
        button.setAttribute('data-clipboard-action', 'copy')
        button.addEventListener('click', function() {
          button.innerText = 'Copied'
        })
        var clipboard = new Clipboard('.copy-code-button', {
          target: function(trigger) {
            return trigger.nextElementSibling
          }
        })
        clipboard.on('success', function(e) {
          e.clearSelection()
        })
        codeBlock.parentNode.insertBefore(button, codeBlock)
      })
    },
    scrollToSection(elementID) {
      this.currentSection = elementID
      document.getElementById(elementID).scrollIntoView({ behavior: 'smooth' })
    },
    formatString(string) {
      return string.toLowerCase().replace(/[^A-Za-z0-9]/g, '-').replace(/\-+/g, '-')
    },
    goToProviderGuides() {
      this.$router.push({
        name: this.$route.name.includes('branded') ? 'provider-info-branded' : 'provider-info',
        params: {
          provider_name: this.$route.params.provider_name,
          tab_name: 'guides'
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.sticky {
  position: -webkit-sticky;
  position: sticky;
  top: 4rem;
}
.icons-right {
  margin-right: 12px;
}
.readme-menu-item {
  margin-top: 12px;
}
.readme-menu-text a {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #505050;
}
.readme-menu-text-link {
  margin-top: 12px;
}
.last-updated-container {
  margin-top: 64px;
}
.more-api-section {
  padding: 20px;
}
@media only screen and (min-width: 992px) {
  .guides {
    .container {
      min-height: 250px;
    }
  }
}
::v-deep .tui-editor-contents {
  h1 { margin-top: 2rem; margin-bottom: 1rem; }
  h2 { margin-top: 1.5rem; margin-bottom: 0.5rem; }
  h3 { margin-top: 1.25rem; margin-bottom: 0.5rem; }
  h4 { margin-top: 1.25rem; margin-bottom: 0.5rem; }
  h5 { margin-top: 1.25rem; margin-bottom: 0.5rem; }
  h6 { margin-top: 1.25rem; margin-bottom: 0.5rem; }
  p { margin: 0px; }
}
.theme-link, .theme-link:hover, .theme-link:active {
  color: #57ABF0;
}
.theme-readme-link-to-provider-section {
  background-color: rgba(87, 171, 240, 0.1);
  border-left: 3px solid #57ABF0;
}
</style>
